import type { CSSProperties } from "react";
import styles from "./ReportTable.module.css";
import { ICategory, IEvaluator, IAnswer } from "../../interfaces/IReport";

interface Props {
  categories: ICategory[];
  evaluatorsByType: {
    selfEvaluation?: IEvaluator;
    teamLeaders: IEvaluator[];
    managers: IEvaluator[];
    colleagues: IEvaluator[];
    friends: IEvaluator[];
    clients: IEvaluator[];
  };
}

export default function ReportTable({ categories, evaluatorsByType }: Props) {
  if (categories.length === 0) {
    return (
      <div className={styles.empty}>
        Nenhuma categoria ou pergunta disponível
      </div>
    );
  }

  const orderedCategories = [...categories].sort((left, right) => {
    const leftOrder = left.order;
    const rightOrder = right.order;

    if (leftOrder == null && rightOrder != null) return 1;
    if (leftOrder != null && rightOrder == null) return -1;
    if (leftOrder != null && rightOrder != null && leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }

    return left.categoryName.localeCompare(right.categoryName);
  });

  const getAnswersByEvaluator = (
    question: ICategory["questions"][0],
    evaluatorId: string
  ) => question.answers.filter((a) => a.evaluatorId === evaluatorId);

  const aggregateQualification = (answers: IAnswer[]) => {
    if (!answers || answers.length === 0) return "-";

    return answers[0]?.qualification || "-";
  };

  // Each group becomes its own report grid, so answers are only compared with
  // evaluators of the relevant type.
  const comparisonGroups = [
    {
      key: "self-team",
      title: "Autoavaliação vs Team Leader vs Management",
      columns: [
        ...(evaluatorsByType.selfEvaluation
          ? [
              {
                label: "Autoavaliação",
                subLabel: evaluatorsByType.selfEvaluation.name,
                evaluatorId: evaluatorsByType.selfEvaluation.id,
              },
            ]
          : []),
        ...evaluatorsByType.teamLeaders.map((tl) => ({
          label: "Team Leader",
          subLabel: tl.name,
          evaluatorId: tl.id,
        })),
        ...evaluatorsByType.managers.map((mgmt) => ({
          label: "Management",
          subLabel: mgmt.name,
          evaluatorId: mgmt.id,
        })),
      ],
    },
    {
      key: "work-colleague",
      title: "Colega trabalho",
      columns: [
        ...evaluatorsByType.colleagues.map((col) => ({
          label: "Colega trabalho",
          subLabel: col.name,
          evaluatorId: col.id,
        })),
      ],
    },
    {
      key: "social-colleague",
      title: "Colega social",
      columns: [
        ...evaluatorsByType.friends.map((fr) => ({
          label: "Colega social",
          subLabel: fr.name,
          evaluatorId: fr.id,
        })),
      ],
    },
    {
      key: "client",
      title: "Cliente",
      columns: [
        ...evaluatorsByType.clients.map((cl) => ({
          label: "Cliente",
          subLabel: cl.clientName || cl.name,
          evaluatorId: cl.id,
        })),
      ],
    },
  ];

  return (
    <div className={styles.container}>
      {comparisonGroups.map((group) => {
        if (group.columns.length === 0) return null;

        const isColleagueGroup =
          group.key === "work-colleague" || group.key === "social-colleague";
        // Text-answer tables always use the mobile card layout. Scored tables
        // use it only when the table has one evaluator.
        const mobileCategorySections = orderedCategories.map((category) => ({
          key: `mobile-${category.categoryId}`,
          category,
          questions:
            group.columns.length === 1
              ? category.questions
              : category.questions.filter(
                  (question) => question.questionType === "Text"
                ),
        }));

        return (
          <div key={group.key} className={styles.comparisonWrapper}>
            {orderedCategories.map((category) => {
              const visibleQuestions = category.questions.filter(
                (q) =>
                  q.questionType !== "Text" &&
                  group.columns.some((col) => {
                    const answers = getAnswersByEvaluator(q, col.evaluatorId);
                    return answers && answers.length > 0;
                  })
              );

              if (visibleQuestions.length === 0) return null;

              return (
                <div
                  key={category.categoryId}
                  className={`${styles.categorySection} ${styles.desktopCategorySection} ${
                    group.columns.length > 1
                      ? styles.legacyMobileCategorySection
                      : ""
                  }`}
                  style={
                    {
                      "--category-order": category.order ?? 9999,
                    } as CSSProperties
                  }
                >
                  <div className={styles.mobileCategoryHeader}>
                    {category.categoryName}
                  </div>
                  <div className={styles.tableWrapper}>
                    <div
                      className={`${styles.reportGrid} ${
                        isColleagueGroup ? styles.colleagueGrid : ""
                      } ${
                        group.columns.length > 1
                          ? styles.multipleEvaluatorGrid
                          : ""
                      }`}
                      role="table"
                      style={
                        {
                          // The CSS grid derives its answer columns from this
                          // count; column 1 is always the question.
                          "--evaluator-count": group.columns.length,
                        } as CSSProperties
                      }
                    >
                      {/* Divs are used for responsive grid layout; ARIA keeps
                          the same table semantics for assistive technology. */}
                      <div className={styles.reportGridHeader} role="rowgroup">
                        <div className={styles.reportGridRow} role="row">
                          <div
                            className={`${styles.categoryHeader} ${
                              isColleagueGroup
                                ? styles.colleagueCategoryHeader
                                : ""
                            }`}
                            role="columnheader"
                          >
                            <span>{category.categoryName}</span>
                            {isColleagueGroup && (
                              <div className={styles.inlineEvaluatorHeaders}>
                                {group.columns.map((col) => (
                                  <div
                                    key={col.evaluatorId}
                                    className={`${styles.evaluatorHeader} ${styles.inlineEvaluatorHeader}`}
                                  >
                                    <span className={styles.evaluatorType}>
                                      {col.label}
                                    </span>
                                    <span className={styles.evaluatorName}>
                                      {col.subLabel}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          {group.columns.map((col) => (
                            <div
                              key={col.evaluatorId}
                              className={`${styles.evaluatorColumn} ${
                                group.columns.length > 1
                                  ? styles.multipleEvaluatorColumn
                                  : ""
                              } ${
                                isColleagueGroup
                                  ? styles.noBorder
                                  : ""
                              }`}
                              role="columnheader"
                            >
                              {!isColleagueGroup && (
                                <div
                                  className={`${styles.evaluatorHeader} ${
                                    group.columns.length > 1
                                      ? styles.multipleEvaluatorHeaders
                                      : ""
                                  }`}
                                >
                                  <span className={styles.evaluatorType}>
                                    {col.label}
                                  </span>
                                  <span className={styles.evaluatorName}>
                                    {col.subLabel}
                                  </span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className={styles.reportGridBody} role="rowgroup">
                        {visibleQuestions.map((question, questionIndex) => (
                          <div
                            key={`${group.key}-${question.questionId}-${questionIndex}`}
                            className={styles.reportGridRow}
                            role="row"
                          >
                            <div className={styles.questionCell} role="cell">
                              <span className={styles.questionText}>
                                {question.questionText || "Pergunta sem título"}
                              </span>
                            </div>

                            {group.columns.map((col) => {
                              const answers = getAnswersByEvaluator(
                                question,
                                col.evaluatorId
                              );
                              return (
                                <div
                                  key={`${question.questionId}-${col.evaluatorId}`}
                                  className={styles.answerCell}
                                  data-evaluator-type={col.label}
                                  data-evaluator-name={col.subLabel}
                                  role="cell"
                                >
                                  <span className={styles.qualification}>
                                    {aggregateQualification(answers)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Text answers use a separate table so long responses can use
                their own cell layout without affecting scored-answer rows. */}
            {orderedCategories.map((category) => {
              const visibleTextQuestions = category.questions.filter(
                (q) =>
                  q.questionType === "Text" &&
                  group.columns.some((col) => {
                    const answers = getAnswersByEvaluator(q, col.evaluatorId);
                    return answers && answers.length > 0;
                  })
              );

              if (visibleTextQuestions.length === 0) return null;

              return (
                <div
                  key={`text-${category.categoryId}`}
                  className={`${styles.categorySection} ${styles.desktopCategorySection}`}
                  style={
                    {
                      "--category-order": category.order ?? 9999,
                    } as CSSProperties
                  }
                >
                  <div className={styles.mobileCategoryHeader}>
                    {category.categoryName}
                  </div>
                  <div className={styles.tableWrapper}>
                    <div
                      className={`${styles.reportGrid} ${styles.textAnswerGrid} ${
                        isColleagueGroup ? styles.colleagueGrid : ""
                      } ${
                        group.columns.length > 1
                          ? styles.multipleEvaluatorGrid
                          : ""
                      }`}
                      role="table"
                      style={
                        {
                          "--evaluator-count": group.columns.length,
                        } as CSSProperties
                      }
                    >
                      <div className={styles.reportGridHeader} role="rowgroup">
                        <div className={styles.reportGridRow} role="row">
                          <div
                            className={`${styles.categoryHeader} ${
                              isColleagueGroup
                                ? styles.colleagueCategoryHeader
                                : ""
                            }`}
                            role="columnheader"
                          >
                            <span>{category.categoryName}</span>
                            {isColleagueGroup && (
                              <div className={styles.inlineEvaluatorHeaders}>
                                {group.columns.map((col) => (
                                  <div
                                    key={col.evaluatorId}
                                    className={`${styles.evaluatorHeader} ${styles.inlineEvaluatorHeader}`}
                                  >
                                    <span className={styles.evaluatorType}>
                                      {col.label}
                                    </span>
                                    <span className={styles.evaluatorName}>
                                      {col.subLabel}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          {group.columns.map((col) => (
                            <div
                              key={col.evaluatorId}
                              className={`${styles.evaluatorColumn} ${
                                group.columns.length > 1
                                  ? styles.multipleEvaluatorColumn
                                  : ""
                              } ${
                                isColleagueGroup ? styles.noBorder : ""
                              }`}
                              role="columnheader"
                            >
                              {!isColleagueGroup && (
                                <div
                                  className={`${styles.evaluatorHeader} ${
                                    group.columns.length > 1
                                      ? styles.multipleEvaluatorHeaders
                                      : ""
                                  }`}
                                >
                                  <span className={styles.evaluatorType}>
                                    {col.label}
                                  </span>
                                  <span className={styles.evaluatorName}>
                                    {col.subLabel}
                                  </span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className={styles.reportGridBody} role="rowgroup">
                        {visibleTextQuestions.map((question, questionIndex) => (
                          <div
                            key={`${group.key}-text-${question.questionId}-${questionIndex}`}
                            className={styles.reportGridRow}
                            role="row"
                          >
                            <div className={styles.questionCell} role="cell">
                              <span className={styles.questionText}>
                                {question.questionText || "Pergunta sem título"}
                              </span>
                            </div>
                            {group.columns.map((col) => {
                              const answers = getAnswersByEvaluator(
                                question,
                                col.evaluatorId
                              );
                              return (
                                <div
                                  key={`${question.questionId}-${col.evaluatorId}`}
                                  className={styles.textAnswerCell}
                                  data-evaluator-type={col.label}
                                  data-evaluator-name={col.subLabel}
                                  role="cell"
                                >
                                  <span className={styles.qualification}>
                                    {aggregateQualification(answers)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            <div className={styles.mobileReport}>
              {mobileCategorySections.map(({ key, category, questions }) => {
                const visibleQuestions = questions.filter((question) =>
                  group.columns.some(
                    (column) =>
                      getAnswersByEvaluator(question, column.evaluatorId).length > 0
                  )
                );

                if (visibleQuestions.length === 0) return null;

                return (
                  <section
                    key={key}
                    className={styles.mobileCategorySection}
                    style={
                      {
                        "--category-order": category.order ?? 9999,
                      } as CSSProperties
                    }
                  >
                    <h3 className={styles.mobileCategoryTitle}>
                      {category.categoryName}
                    </h3>

                    {group.columns.map((column) => {
                      const answeredQuestions = visibleQuestions.filter(
                        (question) =>
                          getAnswersByEvaluator(question, column.evaluatorId).length > 0
                      );

                      if (answeredQuestions.length === 0) return null;

                      return (
                        <div
                          key={column.evaluatorId}
                          className={styles.mobileEvaluatorSection}
                        >
                          <div className={styles.mobileEvaluatorHeader}>
                            <span className={styles.mobileEvaluatorType}>
                              {column.label}
                            </span>
                            <span className={styles.mobileEvaluatorName}>
                              {column.subLabel}
                            </span>
                          </div>

                          {answeredQuestions.map((question, questionIndex) => {
                            const answer = aggregateQualification(
                              getAnswersByEvaluator(question, column.evaluatorId)
                            );

                            if (question.questionType === "Text") {
                              return (
                                <div
                                  key={`${question.questionId}-${questionIndex}`}
                                  className={styles.mobileTextAnswerRow}
                                >
                                  <span className={styles.mobileQuestionText}>
                                    {`${question.questionText || "Pergunta sem título"}`.replace(
                                      /:\s*$/,
                                      ""
                                    )}
                                    :
                                  </span>
                                  {" "}
                                  <span className={styles.mobileTextAnswer}>
                                    {answer}
                                  </span>
                                </div>
                              );
                            }

                            return (
                              <div
                                key={`${question.questionId}-${questionIndex}`}
                                className={styles.mobileScoreRow}
                              >
                                <span className={styles.mobileQuestionText}>
                                  {question.questionText || "Pergunta sem título"}
                                </span>
                                <span className={styles.mobileScore}>{answer}</span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </section>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
